import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import ModalAnimal from '../components/ui/ModalAnimal';
import ModalEvento from '../components/ui/ModalEvento';
import ModalCampania from '../components/ui/ModalCampania';
import ModalGestionUsuario from '../components/ui/ModalGestionUsuario';
import ModalSponsor from '../components/ui/ModalSponsor';
import ImageUpload from '../components/ui/ImageUpload';
import Button from '../components/ui/Button';
import './Panel.css';

// v1.2 - REVISIÓN FINAL DE ETIQUETAS Y PERMISOS
// Si puedes ver este comentario en el inspector, el build se actualizó correctamente.

function ScheduleGrid({ value, onChange, disabled }) {
  const days = ['Lunes', 'Martes', 'Miércoles', 'Jueves', 'Viernes', 'Sábado'];
  const hours = Array.from({ length: 13 }, (_, i) => i + 8);

  const toggle = (day, hour) => {
    if (disabled) return;
    const current = value?.data?.[day] || [];
    const next = current.includes(hour) 
      ? current.filter(h => h !== hour)
      : [...current, hour].sort((a,b) => a-b);
    
    onChange({
      ...value,
      data: { ...value?.data, [day]: next },
      sinHoras: false
    });
  };

  const toggleDay = (day) => {
    if (disabled) return;
    const current = value?.data?.[day] || [];
    const next = current.length > 0 ? [] : hours;
    onChange({
      ...value,
      data: { ...value?.data, [day]: next },
      sinHoras: false
    });
  };

  const toggleNoHours = () => {
    if (disabled) return;
    onChange({ data: {}, sinHoras: !value?.sinHoras });
  };

  const getHourIcon = (h) => {
    if (h < 12) return '☀️';
    if (h < 17) return '🌤️';
    return '🌅';
  };

  return (
    <div className="schedule-container fade-up">
      <div className="schedule-header-actions">
        <div className="flex items-center gap-3">
           <input 
             className="cursor-pointer w-[22px] h-[22px] accent-v"
             type="checkbox" 
             id="no-hours" 
             checked={value?.sinHoras || false} 
             onChange={toggleNoHours} 
           />
           <label htmlFor="no-hours" className="font-extrabold text-[15px] cursor-pointer text-vd">No tengo horas libres este semestre</label>
        </div>
        {value?.sinHoras && (
          <span className="badge badge-gold text-[10px]">DESCANSO ACTIVADO</span>
        )}
      </div>
      
      {value?.sinHoras ? (
        <div className="no-hours-card">
           <div className="no-hours-ico">😴</div>
           <h4 className="m-0 text-txs">Horario en pausa</h4>
           <p className="text-[13px] opacity-60 max-w-[300px] mt-2.5">Has marcado que no tienes disponibilidad. Desmarca la opción superior para volver a seleccionar tus horas.</p>
        </div>
      ) : (
        <div className="schedule-grid">
          <div className="sch-time-label font-black text-[11px] opacity-50 text-center">HORA</div>
          {days.map(d => (
            <div key={d} className="sch-header">
              {d.substring(0,3)}
              <button type="button" className="sch-header-btn" onClick={() => toggleDay(d)}>
                {value?.data?.[d]?.length > 0 ? 'Vaciar' : 'Todos'}
              </button>
            </div>
          ))}
          
          {hours.map(h => (
            <React.Fragment key={h}>
              <div className="sch-time">
                <span>{getHourIcon(h)}</span> <strong>{h}:00</strong>
              </div>
              {days.map(d => {
                const isSelected = value?.data?.[d]?.includes(h);
                return (
                  <div 
                    key={d+h} 
                    className={`sch-cell ${isSelected ? 'on' : ''}`}
                    onClick={() => toggle(d, h)}
                    title={`${d} a las ${h}:00`}
                  />
                );
              })}
            </React.Fragment>
          ))}
        </div>
      )}
      
      {!value?.sinHoras && (
        <div className="mt-5 flex gap-4 justify-center">
          <div className="flex items-center gap-1.5 text-[11px] font-bold opacity-60">
            <div className="w-3 h-3 bg-v rounded-[3px]" /> Disponible
          </div>
          <div className="flex items-center gap-1.5 text-[11px] font-bold opacity-60">
            <div className="w-3 h-3 bg-white border border-vc rounded-[3px]" /> Ocupado
          </div>
        </div>
      )}
    </div>
  );
}

const TABS = [
  { key:'overview',     lbl:'📊 Resumen'      },
  { key:'usuarios',     lbl:'👥 Usuarios'     },
  { key:'donaciones',   lbl:'💰 Donaciones'   },
  { key:'config',       lbl:'⚙️ Ajustes'      },
  { key:'perfil',       lbl:'👤 Perfil'       },
  { key:'animales',     lbl:'🐾 Animales'     },
  { key:'eventos',      lbl:'📅 Actividades'  },
  { key:'reportes',     lbl:'🚨 Reportes'     },
  { key:'voluntarios',  lbl:'🦺 Brigada'      },
  { key:'campanias',    lbl:'🎯 Campañas'    },
  { key:'auspiciantes', lbl:'🏢 Auspiciantes' },
  { key:'logs',         lbl:'📜 Logs'         },
];

const BADGE_ROL = { admin:'badge-admin', maestro:'badge-maestro', finanzas:'badge-maestro', estudiante:'badge-green', publico:'badge-green' };
const LABEL_ROL = { admin:'Admin', maestro:'Maestro', finanzas:'Finanzas', estudiante:'Estudiante', publico:'Público' };

export default function Panel() {
  const { usuario, esAdmin, esFinanzas, esMaestro, updateUsuario, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState(usuario?.rol === 'admin' ? 'overview' : 'perfil');
  const [data, setData] = useState({ usuarios:[], eventos:[], animales:[], donaciones:[], logs:[], reportes:[], voluntarios:[], campanias:[], config:null, sponsors:[] });
  const [loading, setLoading] = useState(false);
  const [showModalAnimal, setShowModalAnimal] = useState(false);
  const [showModalEvento, setShowModalEvento] = useState(false);
  const [showModalCampania, setShowModalCampania] = useState(false);
  const [showModalSponsor, setShowModalSponsor] = useState(false);
  const [selSponsor, setSelSponsor] = useState(null);
  const [previewSponsor, setPreviewSponsor] = useState(null);
  const [selUser, setSelUser] = useState(null);
  const [selAnimal, setSelAnimal] = useState(null);
  const [msgTarget, setMsgTarget] = useState(null); // Para el mensajero
  const [msgText, setMsgText] = useState('');
  
  // Nuevo: Estado para Confirmaciones de Seguridad
  const [confirmData, setConfirmData] = useState(null); 
  const [confirmStep, setConfirmStep] = useState(1);
  const [changingPwd, setChangingPwd] = useState(false);

  const pendientes = (data.animales?.filter(a => !a.validado).length || 0) + (data.eventos?.filter(e => !e.validado).length || 0);

  const handleAccion = async (url, successMsg) => {
    try {
      await api.post(url);
      toast.success(successMsg);
      fetchData();
    } catch (err) {
      toast.error('Ocurrió un error al procesar la solicitud');
    }
  };

  const validarAnimal = (id, aprobado) => api.put(`/animales/${id}/validar`, { aprobado }).then(() => {
    toast.success(aprobado ? 'Animal validado ✅' : 'Propuesta rechazada');
    fetchData();
  }).catch(err => toast.error(err.response?.data?.msg || 'Error al validar animal'));
  const validarEvento = (id, aprobado) => api.put(`/eventos/${id}/validar`, { aprobado }).then(() => { toast.success('Evento validado ✅'); fetchData(); }).catch(() => toast.error('Error al validar evento'));
  const validarReporte = (id) => api.put(`/reportes/${id}/estado`, { estado: 'atendido' }).then(() => { toast.success('Reporte atendido ✅'); fetchData(); }).catch(() => toast.error('Error al validar reporte'));
  const eliminarReporte = (id) => { if (!window.confirm('¿Eliminar este reporte?')) return; api.delete(`/reportes/${id}`).then(() => { toast.success('Reporte eliminado'); fetchData(); }).catch(() => toast.error('Error al eliminar reporte')); };

  const enviarMensaje = async () => {
    if(!msgText.trim()) return;
    try {
      await api.post('/comunicar', { to: msgTarget.email, msg: msgText });
      toast.success(`Mensaje enviado a ${msgTarget.nombre}`);
      setMsgTarget(null);
      setMsgText('');
    } catch { toast.error('Error al enviar'); }
  };
  const [horarioData, setHorarioData] = useState(usuario?.horario || { data: {}, sinHoras: false });

  const fetchData = useCallback(async () => {
    setLoading(true);
    
    // Función segura para fetch individual
    const safeFetch = async (url, defaultVal = []) => {
        try {
            const res = await api.get(url);
            return res.data || defaultVal;
        } catch (e) {
            console.warn(`⚠️ Error fetching ${url}:`, e);
            return defaultVal;
        }
    };

    const [u, e, a, d, c, l, r, v, cp, sp] = await Promise.all([
      esAdmin ? safeFetch('/usuarios') : Promise.resolve([]),
      safeFetch('/eventos?todos=true'),
      safeFetch('/animales?todos=true'),
      (esAdmin || esFinanzas) ? safeFetch('/donaciones') : Promise.resolve([]),
      safeFetch(esAdmin ? '/config/admin' : '/config', {}),
      esAdmin ? safeFetch('/logs') : Promise.resolve([]),
      safeFetch('/reportes'),
      safeFetch('/voluntarios'),
      safeFetch('/campanias'),
      esAdmin ? safeFetch('/auspiciantes') : Promise.resolve([]),
    ]);

    setData({ 
      usuarios:u, eventos:e, animales:a, donaciones:d, config:c, logs:l,
      reportes:r, voluntarios:v, campanias:cp, sponsors:sp 
    });
    setLoading(false);
  }, [esAdmin, esFinanzas]);

  useEffect(() => { if (!usuario) nav('/'); else fetchData(); }, [usuario, nav, fetchData]);

  const updateConfig = (field, val) => api.put('/config', { [field]: val }).then(() => toast.success('Ajuste guardado'));

  const probarConexion = async (tipo) => {
    const t = toast.loading(`Probando conexión con ${tipo === 'mercadopago' ? 'Mercado Pago' : 'Stripe'}...`);
    try {
      const { data } = await api.post(`/config/test-${tipo}`);
      toast.success(data.msg || 'Conexión OK', { id: t });
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Fallo en la conexión', { id: t });
    }
  };

  if (!usuario) return null;
  const ini = (usuario.nombre || usuario.email || '?')[0].toUpperCase();

  return (
    <div className="page-wrap wide fade-up">
      {esMaestro && pendientes > 0 && (
        <div className="panel-sticky-alert slide-down">
          <div className="psa-content">
             <span className="psa-ico">🚨</span>
             <div className="psa-text"><strong>Atención Requerida:</strong> Tienes <b>{pendientes}</b> solicitudes pendientes por validar.</div>
             <div className="psa-btns">
                <button className="btn btn-white btn-sm" onClick={() => setTab('animales')}>Ver Todo</button>
             </div>
          </div>
        </div>
      )}

      {/* HEADER SECTION */}
      <div className="prof-header fade-in">
        <div className="avatar">{ini}</div>
        <div className="prof-info">
          <h1 className="prof-name">{usuario.nombre} {usuario.apellido}</h1>
          <p className="prof-email">{usuario.email} • <span className={`badge badge-${usuario.rol}`}>{usuario.rol}</span></p>
        </div>
        <div className="prof-score">
          <div className="score-val">{usuario.puntos}</div>
          <div className="score-lbl">tu reputación</div>
        </div>
      </div>

      {/* TABS NAVIGATION */}
      <div className="panel-tabs">
        {TABS.filter(t => {
          if (t.key === 'perfil') return true;
          if (esAdmin) return true;
          if (esFinanzas && ['overview', 'donaciones', 'campanias'].includes(t.key)) return true;
          if (esMaestro && ['overview', 'eventos', 'animales', 'reportes'].includes(t.key)) return true;
          return false;
        }).map(t => (
          <button key={t.key} className={`p-tab ${tab===t.key?'on':''}`} onClick={() => setTab(t.key)}>
            {t.lbl}
          </button>
        ))}
      </div>

      {loading && <div className="text-center p-10">Cargando datos...</div>}

      <div className="panel-body">
        {/* TAB: OVERVIEW */}
        {tab === 'overview' && (
          <div className="fade-up">
            <div className="grid-3">
              <div className="card stat-card"><h3>{data.usuarios.length}</h3><p>Usuarios</p></div>
              <div className="card stat-card"><h3>{data.animales.length}</h3><p>Animales</p></div>
              <div className="card stat-card"><h3>{data.donaciones.length}</h3><p>Donaciones</p></div>
            </div>

            {(esAdmin || esMaestro) && pendientes > 0 && (
              <div className="card mt-5 border-t-4 border-[#f59e0b]">
                <h3>📬 Nuevas propuestas por revisar</h3>
                <p className="text-sm opacity-70 mt-0 mb-4">
                  Usuarios enviaron animales o actividades que aún no has validado.
                </p>
                <div className="flex flex-col gap-2.5">
                  {data.animales.filter(a => !a.validado).slice(0, 8).map(a => (
                    <div key={a._id} className="flex flex-wrap items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">
                      <span className="badge badge-r">⏳ Propuesta</span>
                      <strong className="text-sm">{a.nombre}</strong>
                      <span className="text-xs opacity-70">
                        {a.especie} · {a.estado}
                        {a.creadoPor?.nombre ? ` · por ${a.creadoPor.nombre}` : ''}
                      </span>
                      <div className="flex gap-1.5 ml-auto">
                        <button className="btn btn-ghost btn-sm" onClick={() => setSelAnimal(a)}>Ver</button>
                        <button className="btn btn-v btn-sm" onClick={() => validarAnimal(a._id, true)}>✅ Validar</button>
                      </div>
                    </div>
                  ))}
                  {data.eventos.filter(e => !e.validado).slice(0, 5).map(e => (
                    <div key={e._id} className="flex flex-wrap items-center gap-2 bg-sky-50 border border-sky-200 rounded-xl px-3 py-2.5">
                      <span className="badge badge-blue">⏳ Actividad</span>
                      <strong className="text-sm">{e.nombre}</strong>
                      <div className="flex gap-1.5 ml-auto">
                        <button className="btn btn-v btn-sm" onClick={() => validarEvento(e._id, true)}>✅ Validar</button>
                      </div>
                    </div>
                  ))}
                </div>
                <button className="btn btn-outline btn-sm mt-4" onClick={() => setTab('animales')}>
                  Ir a Gestión de Animales →
                </button>
              </div>
            )}
          </div>
        )}

        {/* TAB: DONACIONES */}
        {tab === 'donaciones' && (
          <div className="fade-up">
            <div className="card card-np overflow-x">
              <table className="tbl-fancy">
                <thead><tr><th>Donante</th><th>Monto</th><th>Estado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {data.donaciones.map(d => (
                    <tr key={d._id}>
                      <td>{d.nombreDonante}<br/><small className="opacity-60">{d.metodo}</small></td>
                      <td><strong>${d.monto}</strong></td>
                      <td><span className={`badge ${d.estado === 'pendiente' ? 'badge-gold' : 'badge-green'}`}>{d.estado}</span></td>
                      <td className="flex gap-1">
                        {d.estado === 'pendiente' && (
                          <button className="btn btn-ghost btn-sm" title="Confirmar" onClick={() => handleAccion(`/donaciones/${d._id}/confirmar`, 'Donación confirmada')}>✅</button>
                        )}
                        <button className="btn btn-ghost btn-sm" title="Contactar" onClick={() => setMsgTarget({ nombre: d.nombreDonante, email: d.email || 'donante@correo.com' })}>✉️</button>
                        <button className="btn btn-ghost btn-sm" title="Cancelar" onClick={() => handleAccion(`/donaciones/${d._id}/cancelar`, 'Donación cancelada')}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB: CONFIGURACIÓN (SUPERADMIN) */}
        {tab === 'config' && esAdmin && (
          <div className="fade-up">
            <div className="cfg-intro">
              <div className="cfg-intro-txt">
                <h2 className="sec-title">🔧 Configuración de Pagos y Banco</h2>
                <p>Ajustes de pasarelas, transferencias, correo y donaciones. Los cambios se guardan al salir del campo.</p>
              </div>
            </div>

            <div className="cfg-section">
              <div className="cfg-section-hd">
                <span className="cfg-section-ico">💳</span>
                <div>
                  <h3>Pasarelas de pago</h3>
                  <p>Credenciales de Mercado Pago y Stripe para donaciones en línea.</p>
                </div>
              </div>
              <div className="cfg-pay-grid">
                <div className="cfg-card cfg-card-mp">
                  <div className="cfg-card-hd">
                    <span className="cfg-brand-dot" style={{ background: '#009ee3' }} />
                    <strong>💙 Mercado Pago</strong>
                    <span className={`cfg-pill ${data.config?.mercadopagoConfigured ? 'on' : 'off'}`}>
                      {data.config?.mercadopagoConfigured ? 'configurado' : 'pendiente'}
                    </span>
                  </div>
                  <div className="cfg-field">
                    <label>Public Key</label>
                    <input className="input" defaultValue={data.config?.mercadopagoPublicKey} onBlur={(e) => updateConfig('mercadopagoPublicKey', e.target.value)} />
                  </div>
                  <div className="cfg-field">
                    <label>Access Token</label>
                    <input type="password" className="input" defaultValue={data.config?.mercadopagoAccessToken} onBlur={(e) => updateConfig('mercadopagoAccessToken', e.target.value)} placeholder="APP_USR-..." />
                  </div>
                  <div className="cfg-card-ft">
                    <button className="btn btn-outline-v btn-sm" onClick={() => probarConexion('mercadopago')}>🧪 Probar conexión</button>
                  </div>
                </div>
                <div className="cfg-card cfg-card-st">
                  <div className="cfg-card-hd">
                    <span className="cfg-brand-dot" style={{ background: '#635bff' }} />
                    <strong>💜 Stripe</strong>
                    <span className={`cfg-pill ${data.config?.stripeConfigured ? 'on' : 'off'}`}>
                      {data.config?.stripeConfigured ? 'configurada' : 'pendiente'}
                    </span>
                  </div>
                  <div className="cfg-field">
                    <label>Public Key</label>
                    <input className="input" defaultValue={data.config?.stripePublicKey} onBlur={(e) => updateConfig('stripePublicKey', e.target.value)} />
                  </div>
                  <div className="cfg-field">
                    <label>Secret Key</label>
                    <input type="password" className="input" defaultValue={data.config?.stripeSecretKey} onBlur={(e) => updateConfig('stripeSecretKey', e.target.value)} placeholder="sk_live_..." />
                  </div>
                  <div className="cfg-card-ft">
                    <button className="btn btn-outline-v btn-sm" onClick={() => probarConexion('stripe')}>🧪 Probar conexión</button>
                  </div>
                </div>
              </div>
            </div>

            <div className="cfg-section">
              <div className="cfg-section-hd">
                <span className="cfg-section-ico">🏦</span>
                <div>
                  <h3>Transferencia bancaria</h3>
                  <p>Datos que se muestran al donante para transferencia directa.</p>
                </div>
              </div>
              <div className="cfg-card cfg-card-wide">
                <div className="cfg-grid-3">
                  <div className="cfg-field"><label>Banco</label><input className="input" defaultValue={data.config?.bancoNombre} onBlur={(e) => updateConfig('bancoNombre', e.target.value)} /></div>
                  <div className="cfg-field"><label>CLABE</label><input className="input" defaultValue={data.config?.bancoClabe} onBlur={(e) => updateConfig('bancoClabe', e.target.value)} /></div>
                  <div className="cfg-field"><label>Beneficiario</label><input className="input" defaultValue={data.config?.bancoBeneficiario} onBlur={(e) => updateConfig('bancoBeneficiario', e.target.value)} /></div>
                </div>
              </div>
            </div>

            <div className="cfg-section">
              <div className="cfg-section-hd">
                <span className="cfg-section-ico">📧</span>
                <div>
                  <h3>Correo transaccional</h3>
                  <p>Servicio Resend configurado en el entorno del servidor.</p>
                </div>
              </div>
              <div className="cfg-card cfg-card-wide">
                <div className="cfg-status-row">
                  <span className={`cfg-pill ${data.config?.emailConfigured ? 'on' : 'off'}`}>
                    {data.config?.emailConfigured ? 'configurado en el entorno' : 'pendiente en el entorno'}
                  </span>
                  <span className="cfg-hint">Las API keys se manejan por variables de entorno, no en este formulario.</span>
                </div>
              </div>
            </div>

            <div className="cfg-section">
              <div className="cfg-section-hd">
                <span className="cfg-section-ico">💰</span>
                <div>
                  <h3>Donaciones y puntos</h3>
                  <p>Define los montos sugeridos y cuántos puntos otorga cada nivel.</p>
                </div>
              </div>
              <div className="cfg-card cfg-card-wide">
                <div className="don-tiers-grid">
                  {(data.config?.donationTiers || []).map((t, i) => (
                    <div key={i} className="don-tier-row">
                      <div className="cfg-field">
                        <label>MONTO ($)</label>
                        <input type="number" className="input" defaultValue={t.monto} onBlur={(e) => {
                          const next = [...data.config.donationTiers];
                          next[i].monto = Number(e.target.value);
                          updateConfig('donationTiers', next);
                        }} />
                      </div>
                      <div className="cfg-field">
                        <label>PUNTOS (pts)</label>
                        <input type="number" className="input" defaultValue={t.puntos} onBlur={(e) => {
                          const next = [...data.config.donationTiers];
                          next[i].puntos = Number(e.target.value);
                          updateConfig('donationTiers', next);
                        }} />
                      </div>
                      <button className="btn btn-r btn-sm don-tier-x" title="Eliminar nivel" onClick={() => {
                        const next = data.config.donationTiers.filter((_, idx) => idx !== i);
                        updateConfig('donationTiers', next);
                        setData(d => ({ ...d, config: { ...d.config, donationTiers: next } }));
                      }}>✕</button>
                    </div>
                  ))}
                  <button className="btn btn-outline-v btn-sm btn-full" onClick={() => {
                    const next = [...(data.config?.donationTiers || []), { monto: 100, puntos: 100 }];
                    updateConfig('donationTiers', next);
                    setData(d => ({ ...d, config: { ...d.config, donationTiers: next } }));
                  }}>+ Añadir Nivel de Donación</button>
                </div>
              </div>
            </div>

            <div className="cfg-section cfg-section-warn">
              <div className="cfg-section-hd">
                <span className="cfg-section-ico">⚙️</span>
                <div>
                  <h3>Herramientas de Mantenimiento</h3>
                  <p>Acciones masivas para gestión estacional. Requieren confirmación.</p>
                </div>
              </div>
              <div className="cfg-card cfg-card-wide cfg-card-warn">
                <div className="cfg-action-row">
                  <button className="btn btn-outline-v cfg-action-btn" onClick={() => setConfirmData({
                    titulo: '🎓 Iniciar Nuevo Semestre',
                    msg: 'Borrará todos los horarios de disponibilidad de alumnos.',
                    alerta: 'Esta acción notificará a los alumnos y no se puede deshacer.',
                    cmd: () => handleAccion('/config/new-semester', 'Semestre iniciado con éxito'),
                    btnLabel: 'Borrar Horarios',
                    color: '#d69e2e'
                  })}>🎓 Iniciar Semestre</button>
                  <button className="btn btn-r cfg-action-btn" onClick={() => setConfirmData({
                    titulo: '🧹 Limpieza Operativa',
                    msg: 'Se borrarán Animales, Eventos y Donaciones.',
                    alerta: '⚠️ SE MANTENDRÁN las API Keys y Datos Bancarios.',
                    cmd: () => handleAccion('/config/wipe-operational-data', 'Datos operativos limpiados (APIs preservadas)'),
                    btnLabel: 'Limpiar Datos',
                    color: 'var(--r)'
                  })}>🧹 Limpieza Inteligente</button>
                </div>
              </div>
            </div>

            <div className="cfg-section cfg-section-danger">
              <div className="cfg-section-hd">
                <span className="cfg-section-ico">🔥</span>
                <div>
                  <h3>Zona de Peligro (Reset Total)</h3>
                  <p>Borra toda la base de datos, incluidas claves y configuración.</p>
                </div>
              </div>
              <div className="cfg-card cfg-card-wide cfg-card-danger">
                <button className="btn btn-r btn-full" onClick={() => setConfirmData({
                    titulo: '💣 RESET TOTAL DE FÁBRICA',
                    msg: 'Borrará ABSOLUTAMENTE TODO de la base de datos.',
                    alerta: '🚨 PELIGRO 🚨 Perderás API Keys, Banco y toda la configuración.',
                    cmd: async () => { await api.post('/config/reset'); window.location.href='/'; },
                    btnLabel: 'DESTRUIR TODO',
                    color: '#c53030'
                })}>💣 Reiniciar Base de Datos</button>
              </div>
            </div>
          </div>
        )}

        {/* TAB: PERFIL */}
        {tab === 'perfil' && (
          <div className="fade-up">
            <div className="card">
              <h3>👤 Editar Perfil</h3>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                fd.append('horario', JSON.stringify(horarioData));
                try {
                  const { data } = await api.put('/usuarios/perfil', fd, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                  });
                  updateUsuario(data);
                  toast.success('Perfil actualizado');
                } catch { toast.error('Error'); }
              }}>
                <div className="flex gap-5 mb-5">
                   <div className="flex-1">
                      <label>Nombre</label><input className="input" name="nombre" defaultValue={usuario.nombre} required />
                      <label className="mt-2.5 block">Apellido</label><input className="input" name="apellido" defaultValue={usuario.apellido} />
                   </div>
                </div>
                <label>Mi Disponibilidad</label>
                <ScheduleGrid value={horarioData} onChange={setHorarioData} />

                {esAdmin && (
                  <div className="mt-8 pt-5 border-t border-dashed border-slate-200">
                    <h3 className="text-r">🛡️ Privilegios Sugeridos (Super Admin)</h3>
                    <p className="text-xs opacity-70 mb-2.5">Como administrador principal, puedes gestionar los roles globales aquí.</p>
                    <label>Mi Rol Actual / Privilegio de Cuenta</label>
                    <select 
                      className="input border border-r font-bold" 
                      name="rol" 
                      defaultValue={usuario.rol}
                    >
                      <option value="estudiante">Estudiante</option>
                      <option value="maestro">Maestro</option>
                      <option value="admin">Administrador (Super)</option>
                      <option value="finanzas">Finanzas</option>
                    </select>
                  </div>
                )}

                <button type="submit" className="btn btn-v btn-full mt-5">Guardar Cambios</button>
              </form>
            </div>

            <div className="card mt-10">
              <h3>🔐 Cambiar Contraseña</h3>
              <p className="text-sm opacity-70 mt-0 mb-5">
                Actualiza tu clave de acceso. Necesitarás tu contraseña actual y deberás iniciar sesión de nuevo después del cambio.
              </p>
              <form onSubmit={async (e) => {
                e.preventDefault();
                const fd = new FormData(e.target);
                const oldPassword = String(fd.get('oldPassword') || '');
                const newPassword = String(fd.get('newPassword') || '');
                const confirmPassword = String(fd.get('confirmPassword') || '');

                if (newPassword.length < 6) return toast.error('La nueva contraseña debe tener al menos 6 caracteres');
                if (newPassword !== confirmPassword) return toast.error('Las contraseñas no coinciden');
                if (newPassword === oldPassword) return toast.error('La nueva contraseña debe ser diferente a la actual');

                setChangingPwd(true);
                try {
                  await api.put('/auth/change-password', { oldPassword, newPassword, confirmPassword });
                  // El servidor rota tokenVersion y revoca el JWT actual:
                  // cerramos sesión aquí de forma controlada para que el usuario
                  // vea el mensaje y vuelva a entrar con la clave nueva.
                  toast.success('Contraseña actualizada. Inicia sesión con tu nueva clave.');
                  logout();
                  nav('/');
                } catch (err) {
                  const data = err.response?.data;
                  toast.error(data?.msg || data?.errors?.[0]?.mensaje || 'No se pudo cambiar la contraseña');
                } finally {
                  setChangingPwd(false);
                }
              }}>
                <div className="form-group">
                  <label>Contraseña Actual</label>
                  <input className="input" type="password" name="oldPassword" placeholder="••••••••" autoComplete="current-password" required minLength={6} disabled={changingPwd} />
                </div>
                <div className="flex gap-5">
                  <div className="flex-1">
                    <div className="form-group">
                      <label>Nueva Contraseña</label>
                      <input className="input" type="password" name="newPassword" placeholder="Mínimo 6 caracteres" autoComplete="new-password" required minLength={6} disabled={changingPwd} />
                    </div>
                  </div>
                  <div className="flex-1">
                    <div className="form-group">
                      <label>Confirmar Nueva Contraseña</label>
                      <input className="input" type="password" name="confirmPassword" placeholder="Repite la nueva contraseña" autoComplete="new-password" required minLength={6} disabled={changingPwd} />
                    </div>
                  </div>
                </div>
                <button type="submit" className="btn btn-v btn-full" disabled={changingPwd}>
                  {changingPwd ? 'Actualizando...' : '🔐 Actualizar Contraseña'}
                </button>
              </form>
            </div>
          </div>
        )}

        {/* TAB: USUARIOS (ADMIN) */}
        {tab === 'usuarios' && esAdmin && (
          <div className="fade-up">
            <div className="card card-np overflow-x">
              <table className="tbl-fancy">
                <thead><tr><th>Usuario</th><th>Email</th><th>Puntos</th><th>Acciones</th></tr></thead>
                <tbody>
                  {data.usuarios.map(u => (
                    <tr key={u._id}>
                      <td>
                        <strong>{u.nombre} {u.apellido}</strong><br/>
                        <span className={`badge ${BADGE_ROL[u.rol]}`}>{LABEL_ROL[u.rol]}</span>
                      </td>
                      <td>{u.email}</td>
                      <td><span className="badge badge-gold">{u.puntos} pts</span></td>
                      <td>
                        <button className="btn btn-v btn-sm" onClick={() => setSelUser(u)}>⚙️ Gestionar</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB: ANIMALES */}
        {tab === 'animales' && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-5">
              <h2 className="sec-title">🐾 Gestión de Animales</h2>
              <button className="btn btn-v" onClick={() => setShowModalAnimal(true)}>+ Agregar Animal</button>
            </div>
            <div className="grid-3">
              {[...data.animales]
                .sort((a, b) => Number(!a.validado) - Number(!b.validado) || new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
                .map(a => (
                <div key={a._id} className={`card a-card fade-up${!a.validado ? ' border-t-4 border-amber-400' : ''}`}>
                  <img src={a.fotos?.[0] || 'data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%22150%22%20height%3D%22150%22%3E%3Crect%20width%3D%22150%22%20height%3D%22150%22%20fill%3D%22%23e2e8f0%22%2F%3E%3Ctext%20x%3D%2250%25%22%20y%3D%2254%25%22%20fill%3D%22%2394a3b8%22%20font-size%3D%2222%22%20text-anchor%3D%22middle%22%20dominant-baseline%3D%22middle%22%20font-family%3D%22sans-serif%22%3E%F0%9F%90%BE%3C%2Ftext%3E%3C%2Fsvg%3E'} alt={a.nombre} className="w-full h-[150px] object-cover rounded-xl mb-2.5" />
                  {!a.validado && (
                    <div className="badge badge-r mb-2 self-start">✉️ Nueva propuesta de adopción</div>
                  )}
                  <h3 className="m-0">{a.nombre}</h3>
                  <div className="flex gap-1 mt-1 flex-wrap">
                    <span className="badge badge-green">{a.especie}</span>
                    <span className="badge badge-gold">{a.estado}</span>
                    {!a.validado && <span className="badge badge-r">⏳ En revisión</span>}
                  </div>
                  {a.creadoPor && (
                    <div className="text-xs opacity-70 mt-1.5">
                      Enviado por <strong>{a.creadoPor.nombre} {a.creadoPor.apellido || ''}</strong>
                    </div>
                  )}
                  <div className="flex gap-1 mt-2.5">
                    <button className="btn btn-ghost btn-sm flex-1" onClick={() => setSelAnimal(a)}>Ver Detalles</button>
                    {!a.validado && (
                      <>
                        <button className="btn btn-v btn-sm flex-1" onClick={() => validarAnimal(a._id, true)}>✅ Validar</button>
                        <button className="btn btn-r btn-sm" title="Rechazar propuesta" onClick={() => {
                          if (window.confirm(`¿Rechazar la propuesta de ${a.nombre}? Se eliminará del catálogo.`)) {
                            validarAnimal(a._id, false);
                          }
                        }}>✕</button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB: ACTIVIDADES */}
        {tab === 'eventos' && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-5">
              <h2 className="sec-title">📅 Actividades y Brigadas</h2>
              <button className="btn btn-v" onClick={() => setShowModalEvento(true)}>+ Nueva Actividad</button>
            </div>
            <div className="card card-np overflow-x">
                <table className="tbl-fancy">
                  <thead><tr><th>Actividad</th><th>Fecha</th><th>Lugar</th><th>Acciones</th></tr></thead>
                  <tbody>
                    {data.eventos.map(e => (
                      <tr key={e._id}>
                        <td><strong>{e.nombre}</strong><br/><small>{e.descripcion?.substring(0,30)}...</small></td>
                        <td>{new Date(e.fecha).toLocaleDateString()}</td>
                        <td>{e.lugar}</td>
                        <td>
                          <div className="flex gap-1">
                            <button className="btn btn-outline btn-sm">Ver</button>
                            {!e.validado && (
                              <button className="btn btn-v btn-sm" title="Validar" onClick={() => validarEvento(e._id, true)}>✅ Validar</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
            </div>
          </div>
        )}

        {/* TAB: LOGS (ADMIN) */}
        {tab === 'logs' && esAdmin && (
          <div className="fade-up">
            <h2 className="sec-title">📜 Auditoría del Sistema</h2>
            <div className="card card-np overflow-x">
              <table className="tbl-fancy">
                <thead><tr><th>Fecha</th><th>Usuario</th><th>Acción</th><th>Detalle</th></tr></thead>
                <tbody>
                  {data.logs.map(l => (
                    <tr key={l._id}>
                      <td>{new Date(l.createdAt).toLocaleString()}</td>
                      <td>{l.usuarioId?.nombre || 'Sístema'}</td>
                      <td><span className="badge badge-gold">{l.accion}</span></td>
                      <td><small>{l.descripcion}</small></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        {/* TAB: REPORTES */}
        {tab === 'reportes' && (
          <div className="fade-up">
            <h2 className="sec-title">🚨 Reportes Ciudadanos</h2>
            <div className="card card-np overflow-x">
              <table className="tbl-fancy">
                <thead><tr><th>Imagen</th><th>Incidencia</th><th>Ubicación</th><th>Fecha</th><th>Estado</th><th>Acciones</th></tr></thead>
                <tbody>
                  {data.reportes.map(r => (
                    <tr key={r._id}>
                      <td>
                        {r.foto ? (
                          <img src={r.foto} alt={r.descripcion} style={{ width: 72, height: 54, objectFit: 'cover', borderRadius: 8, display: 'block' }} />
                        ) : (
                          <span className="text-txs">—</span>
                        )}
                      </td>
                      <td><strong>{r.tipoAnimal}</strong><br/><small>{r.descripcion}</small></td>
                      <td>{r.ubicacion}</td>
                      <td>{new Date(r.createdAt).toLocaleDateString()}</td>
                      <td><span className={`badge ${r.estado === 'pendiente' ? 'badge-gold' : 'badge-green'}`}>{r.estado}</span></td>
                      <td className="flex gap-1">
                         <button className="btn btn-ghost btn-sm" title="Validar" onClick={() => validarReporte(r._id)}>✅</button>
                         <button className="btn btn-ghost btn-sm" title="Mensaje" onClick={() => setMsgTarget({ nombre: 'Informante', email: 'reporte@cima.mx' })}>✉️</button>
                         <button className="btn btn-ghost btn-sm" title="Eliminar" onClick={() => eliminarReporte(r._id)}>🗑️</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* TAB: BRIGADA */}
        {tab === 'voluntarios' && (
          <div className="fade-up">
            <h2 className="sec-title">🦺 Brigada de Voluntarios</h2>
            <div className="grid-3">
              {data.usuarios.filter(u => u.horario && Object.keys(u.horario.data || {}).length > 0).map(v => (
                <div key={v._id} className="card fade-up">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="m-0">{v.nombre} {v.apellido}</h3>
                      <p className="m-0 opacity-60">{v.email}</p>
                    </div>
                    <span className="badge badge-green">Disponible</span>
                  </div>
                  <div className="mt-4">
                    <div className="text-xs opacity-50">Horario Configurado</div>
                    <button className="btn btn-v btn-sm btn-full mt-2.5" onClick={() => setMsgTarget(v)}>Invitar a Evento</button>
                  </div>
                </div>
              ))}
              {/* Fallback si no hay brigadistas reales */}
              {data.voluntarios.map(v => (
                <div key={v._id} className="card fade-up opacity-70">
                  <h3 className="m-0">{v.nombre} {v.apellido}</h3>
                  <p className="m-0 opacity-60">{v.email}</p>
                  <button className="btn btn-ghost btn-sm btn-full mt-2.5" onClick={() => setMsgTarget(v)}>✉️ Contactar</button>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TAB: CAMPAÑAS */}
        {tab === 'campanias' && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-5">
              <h2 className="sec-title">🎯 Campañas Activas</h2>
              {esAdmin && <button className="btn btn-v" onClick={() => setShowModalCampania(true)}>+ Nueva Campaña</button>}
            </div>
            <div className="grid-2">
              {data.campanias.map(c => {
                 const pct = Math.min(100, Math.round((c.recaudado / c.meta) * 100));
                 return (
                    <div key={c._id} className="card fade-up">
                      <h3>{c.titulo}</h3>
                      <p>{c.descripcion}</p>
                      <div className="mt-5">
                        <div className="flex justify-between text-xs mb-1">
                          <span>Progreso: {pct}%</span>
                          <span>Meta: ${c.meta}</span>
                        </div>
                        <div className="h-2.5 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full bg-v transition-all duration-1000 w-[${pct}%]`} />
                        </div>
                        <div className="mt-4 text-right font-black">Recaudado: ${c.recaudado}</div>
                      </div>
                    </div>
                 );
              })}
            </div>
          </div>
        )}
        {/* TAB: AUSPICIANTES (ADMIN) */}
        {tab === 'auspiciantes' && esAdmin && (
          <div className="fade-up">
            <div className="flex justify-between items-center mb-5" style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20, flexWrap:'wrap', gap:12 }}>
              <h2 className="sec-title" style={{ marginBottom:0 }}>🏢 Auspiciantes</h2>
              <Button variant="primary" onClick={() => { setSelSponsor(null); setShowModalSponsor(true); }}>
                + Nuevo Auspiciante
              </Button>
            </div>

            {(data.sponsors || []).length === 0 ? (
              <div className="card" style={{ textAlign:'center', padding:40 }}>
                <p style={{ fontSize:40, margin:0 }}>🏢</p>
                <p style={{ fontWeight:700, marginTop:8 }}>Aún no hay auspiciantes</p>
                <p style={{ color:'var(--txs)', fontSize:14 }}>
                  Crea el primero para que aparezca en el popup de bienvenida.
                </p>
              </div>
            ) : (
              <div className="card card-np overflow-x" style={{ overflowX:'auto' }}>
                <table className="tbl-fancy sponsor-tbl">
                  <thead>
                    <tr>
                      <th>Imagen</th>
                      <th>Auspiciante</th>
                      <th>Vigencia</th>
                      <th>Estado</th>
                      <th>Aporte</th>
                      <th>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(data.sponsors || []).map((s) => {
                      const est = s.estado || 'INACTIVO';
                      const badge =
                        est === 'ACTIVO' ? 'badge-green' :
                        est === 'PROGRAMADO' ? 'badge-blue' :
                        est === 'VENCIDO' ? 'badge-red' : 'badge-gold';
                      const fmt = (d) => d ? new Date(d).toLocaleDateString('es-MX', { day:'2-digit', month:'short', year:'numeric' }) : '—';
                      return (
                        <tr key={s._id}>
                          <td>
                            {s.imageUrl ? (
                              <img src={s.imageUrl} alt={s.nombre} className="sponsor-thumb" />
                            ) : (
                              <span style={{ color:'var(--txs)' }}>—</span>
                            )}
                          </td>
                          <td><strong>{s.nombre}</strong></td>
                          <td>
                            <small>{fmt(s.fechaInicio)} → {fmt(s.fechaFin)}</small>
                            {est === 'VENCIDO' && (
                              <div style={{ fontSize:11, color:'var(--r)', fontWeight:700 }}>
                                Venció {fmt(s.fechaFin)}
                              </div>
                            )}
                            {typeof s.diasRestantes === 'number' && est === 'ACTIVO' && (
                              <div style={{ fontSize:11, color:'var(--txs)' }}>
                                {s.diasRestantes} día{s.diasRestantes === 1 ? '' : 's'} restante{s.diasRestantes === 1 ? '' : 's'}
                              </div>
                            )}
                          </td>
                          <td><span className={`badge ${badge}`}>{est}</span></td>
                          <td>
                            <small style={{ color:'var(--txm)', display:'block', maxWidth:180 }}>
                              {s.mensajeAporte ? `Apoyaron con: ${s.mensajeAporte.length > 60 ? s.mensajeAporte.slice(0,60) + '…' : s.mensajeAporte}` : '—'}
                            </small>
                            <small style={{ color:'var(--txs)', fontSize:11 }}>
                              Popup: {s.delaySegundos ?? 60}s
                            </small>
                          </td>
                          <td>
                            <div className="sponsor-actions">
                              <Button variant="ghost" size="sm" onClick={() => { setSelSponsor(s); setShowModalSponsor(true); }} title="Editar">
                                ✏️ Editar
                              </Button>
                              <Button
                                variant={s.active ? 'danger' : 'success'}
                                size="sm"
                                title={s.active ? 'Desactivar' : 'Activar'}
                                onClick={async () => {
                                  try {
                                    await api.put(`/auspiciantes/${s._id}/toggle`);
                                    toast.success(s.active ? 'Desactivado' : 'Activado');
                                    fetchData();
                                  } catch {
                                    toast.error('No se pudo cambiar el estado');
                                  }
                                }}
                              >
                                {s.active ? '⏸ Desactivar' : '▶ Activar'}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => setPreviewSponsor(s)} title="Vista previa">
                                👁 Vista previa
                              </Button>
                              <Button
                                variant="danger"
                                size="sm"
                                title="Eliminar"
                                onClick={async () => {
                                  if (!window.confirm(`¿Eliminar el auspiciante "${s.nombre}"? Esta acción no se puede deshacer.`)) return;
                                  try {
                                    await api.delete(`/auspiciantes/${s._id}`);
                                    toast.success('Auspiciante eliminado');
                                    fetchData();
                                  } catch {
                                    toast.error('No se pudo eliminar');
                                  }
                                }}
                              >
                                🗑 Eliminar
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Vista previa del popup */}
            {previewSponsor && (
              <div className="ad-overlay fade-in" style={{ zIndex:10001 }} onClick={(e) => e.target === e.currentTarget && setPreviewSponsor(null)}>
                <div className="ad-card slide-up" onClick={(e) => e.stopPropagation()}>
                  <button className="ad-close" onClick={() => setPreviewSponsor(null)} aria-label="Cerrar vista previa">✕</button>
                  <div className="ad-badge">Patrocinado · Vista previa</div>
                  <a className="ad-image-link" href={previewSponsor.targetUrl} target="_blank" rel="noopener noreferrer">
                    <img className="ad-image" src={previewSponsor.imageUrl} alt={previewSponsor.nombre} />
                  </a>
                  {previewSponsor.mensajeAporte ? (
                    <>
                      <p className="ad-nombre">{previewSponsor.nombre}</p>
                      <p className="ad-mensaje">Apoyaron con: {previewSponsor.mensajeAporte}</p>
                    </>
                  ) : (
                    <p className="ad-mensaje ad-mensaje-nombre">{previewSponsor.nombre}</p>
                  )}
                  <div className="ad-footer">
                    Así se verá el popup para los alumnos
                    {typeof previewSponsor.delaySegundos === 'number'
                      ? ` · aparece a los ${previewSponsor.delaySegundos}s`
                      : ''}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* MODALS */}
      {showModalAnimal && <ModalAnimal onClose={() => setShowModalAnimal(false)} onCreado={fetchData} />}
      {selAnimal && <ModalAnimal animal={selAnimal} onClose={() => setSelAnimal(null)} onCreado={fetchData} />}
      {showModalEvento && <ModalEvento onClose={() => setShowModalEvento(false)} onCreado={fetchData} />}
      {showModalCampania && <ModalCampania onClose={() => setShowModalCampania(false)} onCreado={fetchData} />}
      {showModalSponsor && (
        <ModalSponsor
          sponsor={selSponsor}
          onClose={() => { setShowModalSponsor(false); setSelSponsor(null); }}
          onSaved={fetchData}
        />
      )}
      {selUser && (
        <ModalGestionUsuario 
            user={selUser} 
            onClose={() => setSelUser(null)} 
            onUpdated={() => { fetchData(); }} 
        />
      )}
      {/* MODAL MENSAJERO */}
      {msgTarget && (
        <div className="modal-overlay" onClick={() => setMsgTarget(null)}>
          <div className="modal-content max-w-[500px]" onClick={e=>e.stopPropagation()}>
            <h2 className="mt-0 flex items-center gap-2.5">✉️ Contactar a {msgTarget.nombre}</h2>
            <p className="opacity-60 text-sm">{msgTarget.email}</p>
            
            <label className="block mb-2.5 font-semibold">Mensaje:</label>
            <textarea 
              className="input resize-none w-full p-2.5 rounded-lg border border-slate-200" 
              rows="6" 
              placeholder="Escribe aquí tu mensaje o solicitud de comprobante..."
              value={msgText}
              onChange={e => setMsgText(e.target.value)}
            />

            <div className="flex gap-2.5 mt-5">
              <button className="btn btn-ghost flex-1" onClick={() => setMsgTarget(null)}>Cancelar</button>
              <button className="btn btn-v flex-[2]" onClick={enviarMensaje}>Enviar Mensaje</button>
            </div>
          </div>
        </div>
      )}

      {/* MODAL DE CONFIRMACIÓN DE SEGURIDAD (ANTI-BLINK) */}
      {confirmData && (
        <div className="modal-overlay backdrop-blur-md z-[100000] bg-black/40">
          <div className="modal-content fade-up max-w-[450px]" style={{ 
            border: `2px solid ${confirmStep === 2 ? '#c53030' : confirmData.color}`,
            boxShadow: confirmStep === 2 ? '0 0 30px rgba(197, 48, 48, 0.3)' : '0 10px 40px rgba(0,0,0,0.1)'
          }}>
            <div className="text-center mb-5">
              <span className="text-[50px]">{confirmStep === 1 ? '⚠️' : '💣'}</span>
              <h2 className={`m-0 mt-2.5 ${confirmStep === 2 ? 'text-red-700' : 'text-slate-800'}`}>
                {confirmStep === 1 ? confirmData.titulo : '¿ESTÁS ABSOLUTAMENTE SEGURO?'}
              </h2>
            </div>
            
            <div className="px-2.5 text-base leading-relaxed">
              {confirmStep === 1 ? (
                <>
                  <p className="font-medium">{confirmData.msg}</p>
                  <p className="text-sm text-slate-500 mt-2.5">{confirmData.alerta}</p>
                </>
              ) : (
                <div className="bg-red-50 p-4 rounded-xl border border-red-200">
                  <p className="m-0 font-bold text-red-900">AVISO CRÍTICO:</p>
                  <p className="m-0 mt-1.5 text-sm">Esta acción eliminará datos operativos permanentemente. No se puede deshacer.</p>
                </div>
              )}
            </div>

            <div className="flex gap-3 mt-8">
              <button 
                className="btn btn-ghost flex-1 p-3" 
                onClick={() => { setConfirmData(null); setConfirmStep(1); }}
              >
                Cancelar
              </button>
              
              {confirmStep === 1 ? (
                <button 
                  className="btn flex-[1.5] text-white font-bold" 
                  style={{ background: confirmData.color }} 
                  onClick={() => setConfirmStep(2)}
                >
                  Entendido, Continuar
                </button>
              ) : (
                <button 
                  className="btn btn-r flex-[1.5] p-2.5 font-black text-base animate-pulse" 
                  onClick={async () => {
                    setLoading(true);
                    await confirmData.cmd();
                    setConfirmData(null);
                    setConfirmStep(1);
                    setLoading(false);
                  }}
                >
                  {confirmData.btnLabel}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
